import { useContext, useState, useEffect } from "react";
import NiceModal from "@ebay/nice-modal-react";
import P from "components/P";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import Center from "layouts/wrapper/Center";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import { useTranslation } from "react-i18next";
import { addCommas } from "shared/utils/Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Dummy from "components/Dummy";
import { toast } from "react-toastify";
import ToolTip from "components/tooltip/tooltip";
import { storeRequester } from "App";

function OrderList({ data: initialOrderData }) {
  const { isMobile } = useContext(BrowserDetectContext);
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState(initialOrderData);

  useEffect(() => {
    setOrderData(initialOrderData);
  }, [initialOrderData]);

  const onPurchaseOrderClick = () => {
    NiceModal.show("purchaseOrder", { data: orderData });
  };

  const getDisplayStatus = (status, payment_status, fulfillment_status) => {
    if (status === "pending" && ["awaiting", "captured"].includes(payment_status)) {
      if (fulfillment_status === "not_fulfilled") {
        return "상품 준비";
      } else if (fulfillment_status === "fulfilled") {
        return "배송 준비";
      } else if (fulfillment_status === "partially_shipped") {
        return "배송 중";
      }
    }
    return status;
  };

  const canCancelOrder = orderData && 
    orderData.status === "pending" && 
    orderData.payment_status === "awaiting" && 
    orderData.fulfillment_status === "not_fulfilled";

  const onCancelOrder = async () => {
    try {
      await storeRequester.cancelCustomerOrder(
        orderData.id,
        (response) => {
          if (response.order) {
            setOrderData(prevOrder => ({
              ...prevOrder,
              status: response.order.status,
              payment_status: response.order.payment_status,
              fulfillment_status: response.order.fulfillment_status
            }));

            toast.success(t("주문이 취소되었습니다."), {
              autoClose: 2000,
              position: toast.POSITION.BOTTOM_CENTER
            });
          } else {
            toast.error(t("주문 취소에 실패했습니다."), {
              autoClose: 2000,
              position: toast.POSITION.BOTTOM_CENTER
            });
          }
        }
      );
    } catch (error) {
      console.error("Cancel order error:", error);
      toast.error(t("주문 취소 중 오류가 발생했습니다."), {
        autoClose: 2000,
        position: toast.POSITION.BOTTOM_CENTER
      });
    }
  };

  const onShippingStatusClick = () => {
    const trackingNumber = orderData.metadata?.shipping?.replace(/-/g, '');

    if (!trackingNumber) {
      toast.error(t("운송장 번호가 존재하지 않습니다."), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    const trackingUrl = `https://trace.cjlogistics.com/next/tracking.html?wblNo=${trackingNumber}`;
    const popupOptions = "width=800,height=800,scrollbars=yes,resizable=yes";
    window.open(trackingUrl, "ShippingStatusPopup", popupOptions);
  };

  return (
    <HorizontalFlex padding={"10px 0"} borderBottom={"1px solid lightgray"}>
      <FlexChild width={isMobile ? "100px" : "150px"} justifyContent={"center"}>
        <Center>
          <P>
            {orderData && orderData.created_at.replace("T", " ").slice(0, 10)}
          </P>
          <P onClick={onPurchaseOrderClick} cursor color={"#999"} size={"10pt"}>
            {t("detailInfo")}
          </P>
        </Center>
      </FlexChild>
      <FlexChild justifyContent={"center"}>
        {orderData?.items?.length > 0 && (
          <HorizontalFlex margin={"10px 0"}>
            {!isMobile && (
              <FlexChild justifyContent={"flex-start"} width={"initial"}>
                <div>
                  <img width={"100px"} src={orderData.items[0].thumbnail} alt="Product thumbnail" />
                </div>
              </FlexChild>
            )}
            <FlexChild>
              <Center>
                <P>
                  {orderData.items[0].title} 외 {orderData.items.length - 1}개
                </P>
              </Center>
            </FlexChild>
          </HorizontalFlex>
        )}
      </FlexChild>
      {orderData ? (
        <>
          {!isMobile && (
            <Center width={"150px"}>
              <P size={12}>
                {`${orderData.shipping_address?.address_1} \n(${orderData.shipping_address?.address_2})`}
              </P>
              <P size={12}>
                우편번호 : {`[${orderData.shipping_address?.postal_code}]`}
              </P>
              <ToolTip style={{zIndex:100}} title={
                <P style={{backgroundColor:'white'}} size={12}>
                  {orderData?.message ? orderData.message : "없음"}
                </P>
              }>
                <P ellipsis size={12}>
                  {`배송메세지 : [${orderData?.message ? orderData.message : "없음"}]`}
                </P>
              </ToolTip>
            </Center>
          )}
        </>
      ) : null}
      {!isMobile && (
        <FlexChild width={"100px"} justifyContent={"center"}>
          <P weight={"bold"} color={"var(--main-color)"} padding={"20px"}>
            {addCommas(orderData?.total)}&#8361;
          </P>
        </FlexChild>
      )}

      <FlexChild width={"100px"} justifyContent={"center"}>
        <Center>
          {orderData && (
            <P>
              {t(getDisplayStatus(
                orderData.status,
                orderData.payment_status,
                orderData.fulfillment_status
              ))}
            </P>
          )}
          <Dummy height={"5px"} />
          {canCancelOrder && (
            <div 
              onClick={onCancelOrder} 
              style={{ 
                cursor: "pointer", 
                display: "flex", 
                backgroundColor: "var(--main-color)", 
                padding: "3px 10px", 
                justifyContent: "center", 
                alignItems: "center",
                marginBottom: "5px"
              }}
            >
              <P color={"white"}>{t("주문취소")}</P>
            </div>
          )}
          {orderData && (orderData.status === "배송 중" || orderData.status === "completed") && (
            <div 
              onClick={onShippingStatusClick} 
              style={{ 
                cursor: "pointer", 
                display: "flex", 
                backgroundColor: "var(--main-color)", 
                padding: "3px 10px", 
                justifyContent: "center", 
                alignItems: "center" 
              }}
            >
              <FontAwesomeIcon icon={faSearch} color={"white"} size="sm" />
              <Dummy width={"10px"} />
              <P color={"white"}>배송조회</P>
            </div>
          )}
        </Center>
      </FlexChild>
    </HorizontalFlex>
  );
}

export default OrderList;